// -- App.js

import { Suspense, lazy, useEffect } from "react";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";

import "./App.css"; // <-- Ensure this line exists at the top of your App.js file

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

import { Navigate } from "react-router-dom";
import EnvironmentInfo from "./components/EnvironmentInfo";
import BuildRefreshGate from "./components/BuildRefreshGate";
import ShareJoinAttribution from "./components/shareCampaign/ShareJoinAttribution";
import { CrashRouteObserver } from "./components/crash/AppCrashLifecycle";
import { VisitMarginExperimentProvider } from "./components/experiments/VisitMarginExperiment";
import { BbbBrandingExperimentProvider } from "./components/experiments/BbbBrandingExperiment";
import ScrollToTop from "./components/ScrollToTop";
import { legalDocuments } from "./data/legalDocuments";
import useInstalledPwaPortraitLock from "./hooks/useInstalledPwaPortraitLock";
import AppRuntimeProviders from "./providers/AppRuntimeProviders";
import { SnackbarProvider } from "./providers/SnackbarProvider";
import { StickyBarHost } from "./providers/StickyBarProvider";
import { captureAdAttributionFromUrl } from "./services/adAttribution";
import { getExperimentPreviewState } from "./services/experiments/previewMode";
import { configureGoogleAdsTag } from "./services/googleAdsConversionTracking";
import { configureMetaPixel, trackMetaEvent } from "./services/metaCapi";
import { redactSensitiveRoutePathname } from "./services/routePath";
import {
  getMemberSeoTaskRootRoutes,
  getMemberSeoTaskSlugs,
} from "./utils/memberSeoRoutes";

import { PUBLIC_PUZZLES_ENABLED } from "./config/puzzlesFeature";

const PuzzlesPage = lazy(() => import("./pages/public/PuzzlesPage"));
const Home = lazy(() => import("./pages/public/Home"));
const SharePage = lazy(() => import("./pages/public/SharePage"));
const ShareQrLanding = lazy(() => import("./pages/public/ShareQrLanding"));
const Landing = lazy(() => import("./components/landing/Landing"));
const AboutPage = lazy(() => import("./pages/public/AboutPage"));
const ContactPage = lazy(() => import("./pages/public/ContactPage"));
const MembershipPage = lazy(() => import("./pages/public/MembershipPage"));
const ConfirmationPage = lazy(() => import("./pages/public/ConfirmationPage"));
const LocationsPage = lazy(() => import("./pages/public/LocationsPage"));
const MapsPage = lazy(() => import("./pages/public/MapsPage"));
const NotFound = lazy(() => import("./pages/public/NotFound"));
const TaskSearchPage = lazy(() => import("./pages/public/TaskSearchPage"));
const ServicesPage = lazy(() => import("./pages/public/ServicesPage"));
const ServiceDetailPage = lazy(
  () => import("./pages/public/ServiceDetailPage"),
);
const LoginOrSignUp = lazy(
  () => import("./components/memberLoginOrSignUp/LoginOrSignUp"),
);
const AdCopyPage = lazy(() => import("./pages/AdCopyPage"));
const AdsView = lazy(() => import("./components/ads/AdsView"));
const ReviewView = lazy(() => import("./components/generate/ReviewView"));
const GeneralLogin = lazy(() => import("./components/login/GeneralLogin"));
const AgentConnectPage = lazy(() => import("./pages/AgentConnectPage"));
const MemberSignUpPage = lazy(
  () => import("./pages/memberFlow/MemberSignUpPage"),
);
const FaqPage = lazy(() => import("./pages/public/FaqPage"));
const HiringPage = lazy(() => import("./pages/public/HiringPage"));
const HiringLocationsHubPage = lazy(() =>
  import("./pages/public/HiringLocationsPage").then((module) => ({
    default: module.HiringLocationsHubPage,
  })),
);
const HiringStatePage = lazy(() =>
  import("./pages/public/HiringLocationsPage").then((module) => ({
    default: module.HiringStatePage,
  })),
);
const HiringCityPage = lazy(() =>
  import("./pages/public/HiringLocationsPage").then((module) => ({
    default: module.HiringCityPage,
  })),
);
const MemberSeoCityPage = lazy(() =>
  import("./pages/public/MemberSeoLandingPage").then((module) => ({
    default: module.MemberSeoCityPage,
  })),
);
const MemberSeoTaskRootPage = lazy(() =>
  import("./pages/public/MemberSeoLandingPage").then((module) => ({
    default: module.MemberSeoTaskRootPage,
  })),
);
const HiringFlyer = lazy(() => import("./components/hiring/HiringFlyer"));
const SeniorFlyer = lazy(() => import("./components/flyers/SeniorFlyer"));
const InvitesPage = lazy(() => import("./pages/public/InvitesPage"));
const AuthPage = lazy(() => import("./components/auth/AuthPage"));
const ReviewPage = lazy(() => import("./pages/public/ReviewPage"));
const ReviewsPage = lazy(() => import("./pages/public/ReviewsPage"));
const HelpersPage = lazy(() => import("./components/helpers/HelpersPage"));
const HelperSignupView = lazy(
  () => import("./components/signup/HelperSignupView"),
);
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ExperimentPreviewConsole = lazy(
  () => import("./components/experiments/ExperimentPreviewConsole"),
);
const DrawerContainer = lazy(
  () => import("./components/drawers/DrawerContainer"),
);
const IngenuityAdminsPage = lazy(
  () => import("./pages/lab/IngenuityAdminsPage"),
);
const LocalUiLabIndexPage = lazy(
  () => import("./pages/lab/LocalUiLabIndexPage"),
);
const LoadingUserLabPage = lazy(
  () => import("./pages/lab/LoadingUserLabPage"),
);
const SeniorAgentBookingPage =
  process.env.NODE_ENV === "development"
    ? lazy(() => import("./pages/lab/SeniorAgentBookingPage"))
    : null;
const HelperFounderWelcomeLabPage = lazy(
  () => import("./pages/lab/HelperFounderWelcomeLabPage"),
);
const HelperSearchGridLabPage = lazy(
  () => import("./pages/lab/HelperSearchGridLabPage"),
);
const GooglePlacesAutocompleteLabPage = lazy(
  () => import("./pages/lab/GooglePlacesAutocompleteLabPage"),
);
const HelperMemberCardActionsLabPage = lazy(
  () => import("./pages/lab/HelperMemberCardActionsLabPage"),
);
const ManualCreateVisitLabPage = lazy(
  () => import("./pages/lab/ManualCreateVisitLabPage"),
);
const LocalHelperMomentumLabPage = lazy(
  () => import("./pages/lab/LocalHelperMomentumLabPage"),
);
const HelperReferralsTabLabPage = lazy(
  () => import("./pages/lab/HelperReferralsTabLabPage"),
);
const FlyerChallengeLabPage = lazy(
  () => import("./pages/lab/FlyerChallengeLabPage"),
);
const HelperDiscountsLabPage = lazy(
  () => import("./pages/lab/HelperDiscountsLabPage"),
);
const HelperRichProfileLabPage = lazy(
  () => import("./pages/lab/HelperRichProfileLabPage"),
);
const HelperRateCardLabPage = lazy(
  () => import("./pages/lab/HelperRateCardLabPage"),
);
const HelperRateSettingsLabPage = lazy(
  () => import("./pages/lab/HelperRateSettingsLabPage"),
);
const LocalPricingDistributionLabPage = lazy(
  () => import("./pages/lab/LocalPricingDistributionLabPage"),
);
const HelperAreaStatusLabPage = lazy(
  () => import("./pages/lab/HelperAreaStatusLabPage"),
);
const HelperRateRewardsLabPage = lazy(
  () => import("./pages/lab/HelperRateRewardsLabPage"),
);
const VisitHelperRateLabPage = lazy(
  () => import("./pages/lab/VisitHelperRateLabPage"),
);
const InAppOrientationPlaygroundPage = lazy(
  () => import("./pages/lab/InAppOrientationPlaygroundPage"),
);
const HiringDeclineLabPage = lazy(
  () => import("./pages/lab/HiringDeclineLabPage"),
);
const HelperMerchOptionalPlaygroundPage = lazy(
  () => import("./pages/lab/HelperMerchOptionalPlaygroundPage"),
);
const ParentAuthorizationLabPage = lazy(
  () => import("./pages/lab/ParentAuthorizationLabPage"),
);
const LegalUiChangesLabPage = lazy(
  () => import("./pages/lab/LegalUiChangesLabPage"),
);
const ParentAuthorizationPage = lazy(
  () => import("./pages/public/ParentAuthorizationPage"),
);
const MemberStampCardLabPage = process.env.NODE_ENV === "development"
  ? lazy(() => import("./pages/lab/MemberStampCardLabPage"))
  : null;
const MemberFounderWelcomeCardLabPage = lazy(
  () => import("./pages/lab/MemberFounderWelcomeCardLabPage"),
);
const MemberHomeAllCardsLabPage = process.env.NODE_ENV === "development"
  ? lazy(() => import("./pages/lab/MemberHomeAllCardsLabPage"))
  : null;
const MemberOnboardingFlowLabPage = lazy(
  () => import("./pages/lab/MemberOnboardingFlowLabPage"),
);
const MemberAddressFlowLabPage = lazy(
  () => import("./pages/lab/MemberAddressFlowLabPage"),
);
const MemberCoreInfoRepairLabPage = lazy(
  () => import("./pages/lab/MemberCoreInfoRepairLabPage"),
);
const MemberDashboardV2LabPage = lazy(
  () => import("./pages/lab/MemberDashboardV2LabPage"),
);
const ShareCampaignLabPage = lazy(
  () => import("./pages/lab/ShareCampaignLabPage"),
);
const MemberBillingVisitsLabPage = lazy(
  () => import("./pages/lab/MemberBillingVisitsLabPage"),
);
const MemberExperienceTelemetryLabPage = lazy(
  () => import("./pages/lab/MemberExperienceTelemetryLabPage"),
);
const ScheduleVisitFlowLabPage = lazy(
  () => import("./pages/lab/ScheduleVisitFlowLabPage"),
);
const CheckoutVariationsLabPage = lazy(
  () => import("./pages/lab/CheckoutVariationsLabPage"),
);
const MemberFirstBookingExperimentsLabPage = lazy(
  () => import("./pages/lab/MemberFirstBookingExperimentsLabPage"),
);
const MembershipCheckoutSummaryLabPage = lazy(
  () => import("./pages/lab/MembershipCheckoutSummaryLabPage"),
);
const MemberPWYCCardLabPage = lazy(
  () => import("./pages/lab/MemberPWYCCardLabPage"),
);
const AnnualMembershipContributionLabPage = lazy(
  () => import("./pages/lab/AnnualMembershipContributionLabPage"),
);
const LandingSectionOrderLabPage = lazy(
  () => import("./pages/lab/LandingSectionOrderLabPage"),
);
const LandingPwycPricingLabPage = lazy(
  () => import("./pages/lab/LandingPwycPricingLabPage"),
);
const PreOnboardingChooseRateLabPage = lazy(
  () => import("./pages/lab/PreOnboardingChooseRateLabPage"),
);
const PreOnboardingV2MembershipLabPage = lazy(
  () => import("./pages/lab/PreOnboardingV2MembershipLabPage"),
);
const PreOnboardingHowItWorksLabPage = lazy(
  () => import("./pages/lab/PreOnboardingHowItWorksLabPage"),
);
const PreOnboardingLocationAvailabilityLabPage = lazy(
  () => import("./pages/lab/PreOnboardingLocationAvailabilityLabPage"),
);
const MemberMembershipRetentionLabPage = lazy(
  () => import("./pages/lab/MemberMembershipRetentionLabPage"),
);
const PreOnboardingAccountContactCardLabPage = lazy(
  () => import("./pages/lab/PreOnboardingAccountContactCardLabPage"),
);
const PreOnboardingWaitlistFlowLabPage = lazy(
  () => import("./pages/lab/PreOnboardingWaitlistFlowLabPage"),
);
const PreOnboardingV2StatesLabPage = lazy(
  () => import("./pages/lab/PreOnboardingV2StatesLabPage"),
);
const PreOnboardingV3LabPage = lazy(
  () => import("./pages/lab/PreOnboardingV3LabPage"),
);
const MemberBillingRateLabPage = lazy(
  () => import("./pages/lab/MemberBillingRateLabPage"),
);
const PublicReviewDetailsLabPage = lazy(
  () => import("./pages/lab/PublicReviewDetailsLabPage"),
);
const PublicHeroPhotoOptionsLabPage = lazy(
  () => import("./pages/lab/PublicHeroPhotoOptionsLabPage"),
);
const PublicChatComposerLabPage = lazy(
  () => import("./pages/lab/PublicChatComposerLabPage"),
);
const PublicDemandMapLabPage = lazy(
  () => import("./pages/lab/PublicDemandMapLabPage"),
);
const CredibilityCountersLabPage = process.env.NODE_ENV === "development"
  ? lazy(() => import("./pages/lab/CredibilityCountersLabPage"))
  : null;
const HelperSelectivityConceptsLabPage = lazy(
  () => import("./pages/lab/HelperSelectivityConceptsLabPage"),
);
const LandingHelperDiscountStickyBarLabPage = lazy(
  () => import("./pages/lab/LandingHelperDiscountStickyBarLabPage"),
);
const LandingCredibilityStickyBarLabPage = lazy(
  () => import("./pages/lab/LandingCredibilityStickyBarLabPage"),
);
const LoginRecoveryLabPage = lazy(
  () => import("./pages/lab/LoginRecoveryLabPage"),
);
const StickyBarSystemLabPage = lazy(
  () => import("./pages/lab/StickyBarSystemLabPage"),
);
const FacebookReviewImageLabPage = lazy(
  () => import("./pages/lab/FacebookReviewImageLabPage"),
);
const SupportMessagePreviewPage = lazy(
  () => import("./pages/lab/SupportMessagePreviewPage"),
);
const MemberMessageSurfacesLabPage = lazy(
  () => import("./pages/lab/MemberMessageSurfacesLabPage"),
);
const DashboardFeedbackLabPage = lazy(
  () => import("./pages/lab/DashboardFeedbackLabPage"),
);
const ScheduleVisitExitFeedbackLabPage = lazy(
  () => import("./pages/lab/ScheduleVisitExitFeedbackLabPage"),
);
const VisitPhotoMediaLabPage = lazy(
  () => import("./pages/lab/VisitPhotoMediaLabPage"),
);
const TieredGeoRetargetingLabPage = lazy(
  () => import("./components/retargetingGeo/TieredGeoRetargetingLabPage"),
);
const LegalDocumentPage = lazy(() => import("./pages/legal/LegalDocumentPage"));
const LegalIndexPage = lazy(() => import("./pages/legal/LegalIndexPage"));
const LegalLayout = lazy(() => import("./pages/legal/LegalLayout"));

const localUiLabs = [
  ...(process.env.NODE_ENV === "development"
    ? [{
        path: "/lab/ui/dashboard-sign-in-loading",
        title: "Dashboard sign-in loading",
        description:
          "Preview the production loading screen shown while the dashboard signs in.",
      }]
    : []),
  {
    path: "/lab/ui/platform/google-places-autocomplete",
    title: "Google Places autocomplete",
    description:
      "Verify every production address and location field with the live Places suggestion menu.",
  },
  {
    path: "/lab/ui/helper-search-grid",
    title: "Helper Search Grid",
    description:
      "Inspect city-level helper availability probe points over live Geocoding viewport fixtures.",
  },
  {
    path: "/lab/ui/helper-discounts",
    title: "Helper-Funded First-Visit Discounts",
    description:
      "Preview all public, member, helper, checkout, billing, and visit-card discount states with local fixture data.",
  },
  {
    path: "/lab/ui/helpers/rich-profile",
    title: "Helper photos",
    description: "Preview the production helper photo card, editor, and member gallery.",
  },
  {
    path: "/lab/ui/helpers/in-app-orientation",
    title: "In-App Orientation",
    description: "Preview helper onboarding orientation states and room QA.",
  },
  {
    path: "/lab/ui/helpers/optional-merch-final-media",
    title: "Merch Settings And Final Media",
    description:
      "Review onboarding without merch, the Settings-only merch link, and the plain blue-or-white final-media rule.",
  },
  {
    path: "/lab/ui/helpers/parent-authorization",
    title: "Parent Authorization",
    description:
      "Review the compact helper Home reminder, recovery actions, and the public parent acceptance page.",
  },
  {
    path: "/lab/ui/helper-founder-welcome",
    title: "Helper Founder Welcome",
    description:
      "Preview the helper startup founder video popup for interview and onboarding transitions.",
  },
  ...(process.env.NODE_ENV === "development"
    ? [{
        path: "/lab/ui/member/stamp-card",
        title: "Member punch card",
        description: "Production punch card, checklist, notifications, and hour credit.",
      }]
    : []),
  {
    path: "/lab/ui/member/founder-welcome-card",
    title: "Founder welcome note",
    description:
      "Preview the member dashboard welcome card and founder note dialog.",
  },
  {
    path: "/lab/ui/member/onboarding-flow",
    title: "Member Onboarding Flow",
    description:
      "Preview the real member onboarding dialog with fixture-backed steps, including Pay What You Can pricing.",
  },
  {
    path: "/lab/ui/member/address-flow",
    title: "Member Address Flow",
    description:
      "Preview pre-onboarding address capture, required address gates, autocomplete fill, and re-geocode behavior.",
  },
  {
    path: "/lab/ui/member/core-info-repair",
    title: "Member Core Info Repair",
    description:
      "Preview the first-login repair gate for auth-only or partially missing member accounts.",
  },
  {
    path: "/lab/ui/member/dashboard-v2",
    title: "Member Dashboard V2",
    description:
      "Preview the dashboard-first new member checklist, pay-now membership drawer, and right priority slot layout.",
  },
  {
    path: "/lab/ui/member/billed-visits",
    title: "Member Billed Visits",
    description:
      "Preview the compact Billing tab visit list, invoice/refund actions, full-list drawer, and empty state.",
  },
  {
    path: "/lab/ui/member/experience-telemetry",
    title: "Member Experience Telemetry",
    description:
      "Preview the real first-login password activation card, telemetry-wrapped dashboard cards, and schedule visit flow with fixture providers.",
  },
  {
    path: "/lab/ui/member/schedule-visit-flow",
    title: "Schedule Visit Flow",
    description:
      "Preview the real schedule visit calendar, helper selector, review, membership, payment, and checkout states with fixture-driven availability and discounts.",
  },
  {
    path: "/lab/ui/member/checkout-variations",
    title: "Checkout Variations",
    description:
      "Step through production checkout states for membership, helper count, discounts, and local credibility.",
  },
  {
    path: "/lab/ui/member/first-booking-experiments",
    title: "First Booking Experiments",
    description:
      "Preview the reservation guard, highlighted-helper booking CTA, helper-lobby arms, one-hour default, and one-helper default with fixture-backed availability.",
  },
  {
    path: "/lab/ui/member/schedule-visit-exit-feedback",
    title: "Schedule Visit Exit Feedback",
    description:
      "Preview the real responsive exit feedback component with rotating checkbox reasons and optional written feedback.",
  },
  {
    path: "/lab/ui/billing/membership-checkout-summary",
    title: "Membership Checkout Summary",
    description:
      "Preview the V2 membership checkout summary with same-day discount and standard pricing states.",
  },
  {
    path: "/lab/ui/member/pay-what-you-can-card",
    title: "Member Pay What You Can Card",
    description:
      "Preview the real member onboarding Pay What You Can pricing card with local fixture data.",
  },
  {
    path: "/lab/ui/member/annual-membership-contribution",
    title: "Annual Membership Contribution",
    description:
      "Preview the production annual Pay What You Can membership card across contribution, zero, reminder, and renewal states.",
  },
  {
    path: "/lab/ui/public/landing-pwyc-pricing",
    title: "Landing PWYC Pricing",
    description:
      "Compare the current membership card with one combined pricing explanation in the production landing section.",
  },
  {
    path: "/lab/ui/member/membership-retention-card",
    title: "Member Membership Retention Card",
    description:
      "Preview the embedded member onboarding Pay What You Can membership price picker and applied state.",
  },
  {
    path: "/lab/ui/pre-onboarding/v2-booking-states",
    title: "Pre-Onboarding V2 Booking States",
    description:
      "Inspect the real V2 checkout, validation, loading, error, retry, recovery, and confirmation components with safe fixtures.",
  },
  {
    path: "/lab/ui/pre-onboarding/v2-membership?arm=pwyc&walkthrough=1",
    title: "Pre-Onboarding V2 PWYC Walkthrough",
    description:
      "Choose an annual contribution, set an hourly rate, and continue into fixture-backed first-visit booking using production components.",
  },
  {
    path: "/lab/ui/pre-onboarding/account-contact-card",
    title: "Pre-Onboarding Account Contact Card",
    description:
      "Preview the combined email/contact card states for typo suggestions, existing email, duplicate phone, validation, and loading.",
  },
  {
    path: "/lab/ui/pre-onboarding/waitlist-flow",
    title: "Pre-Onboarding Waitlist Flow",
    description:
      "Preview the unavailable-location waitlist CTA and waitlist-specific pre-onboarding card order.",
  },
  {
    path: "/lab/ui/pre-onboarding/location-availability",
    title: "Pre-Onboarding Location Availability",
    description:
      "Preview the location card availability status block across upcoming, mobile, loading, limited, and waitlist states.",
  },
  {
    path: "/lab/ui/helpers/member-card-actions",
    title: "Helper Member Card Actions",
    description:
      "Preview the helper-facing member card top actions, Report menu, and hide/ban confirmation states.",
  },
  {
    path: "/lab/ui/helpers/manual-create-visit",
    title: "Manual Create Visit",
    description:
      "Preview the employee/helper manual-create visit dialog with a fixed current-helper assignment.",
  },
  {
    path: "/lab/ui/helpers/local-momentum",
    title: "Local Helper Momentum",
    description:
      "Preview city growth context above small active and starting-soon helper lists.",
  },
  {
    path: "/lab/ui/helpers/referrals-tab",
    title: "Helper Referrals Tab",
    description:
      "Preview the real helper Referrals tab and Flyer Tools drawer entry path.",
  },
  {
    path: "/lab/ui/helpers/flyer-challenge",
    title: "Flyer Challenge",
    description:
      "Preview the resurrected daily flyer challenge, leaderboard, post grid, and mocked upload states.",
  },
  {
    path: "/lab/chat",
    title: "Public Chat Composer",
    description:
      "Compare live public support chat composer layouts for mobile Safari keyboard behavior.",
  },
  {
    path: "/lab/ui/public/hero-photo-options",
    title: "Public Hero Photo Options",
    description:
      "Compare the recovered hero carousel against two real-photo layouts for the public landing hero.",
  },
  {
    path: "/lab/ui/public/demand-map",
    title: "Public Demand Map",
    description:
      "Inspect the embedded public availability map with fixed Anacortes production demand points.",
  },
  ...(process.env.NODE_ENV === "development" ? [{
    path: "/lab/ui/public/credibility-counters",
    title: "Credibility Counters",
    description: "Compare production counter styles and rolling number motion.",
  }] : []),
  {
    path: "/lab/ui/public/helper-selectivity",
    title: "Helper Selectivity Concepts",
    description:
      "Review the landing section concept that shows how selective helper approval is.",
  },
  {
    path: "/lab/ui/public/helper-discount-sticky-bar",
    title: "Landing Helper Discount Sticky Bar",
    description:
      "Preview the reusable helper-discount sticky bar when an active helper offer exists.",
  },
  {
    path: "/lab/ui/public/credibility-sticky-bar",
    title: "Local Credibility Sticky Bar",
    description: "Preview the production local credibility sticky bar with market fixture inputs.",
  },
  {
    path: "/lab/ui/login-recovery",
    title: "Login Recovery",
    description:
      "Preview the unknown-email recovery collapse on the login page.",
  },
  {
    path: "/lab/ui/sticky-bar-system",
    title: "Sticky Bar System",
    description:
      "Preview the global sticky bar provider, host, overlay policy, drawer integration, and viewport helpers.",
  },
  {
    path: "/lab/ui/public/review-detail-lines",
    title: "Public Review Detail Lines",
    description:
      "Preview the public review-card helper/member detail lines across loading, resolved, unavailable, and selected-line states.",
  },
  {
    path: "/lab/ui/public/facebook-review-image",
    title: "Facebook Review Image",
    description:
      "Preview the square Facebook review snapshot with hydrated helper/member details.",
  },
  {
    path: "/lab/ui/messages/support",
    title: "Support Message Media",
    description:
      "Preview support and visit-chat media messages with resolver-backed images, PDFs, processing video, blocked media, and legacy recovery states.",
  },
  {
    path: "/lab/ui/messages/member-surfaces",
    title: "Member Support Surfaces",
    description:
      "Preview member unread support home card and incoming-message snackbar surfaces across desktop and mobile placements.",
  },
  {
    path: "/lab/ui/dashboard-feedback",
    title: "Dashboard Feedback",
    description:
      "Preview the shared production member/helper feedback home card and responsive drawer.",
  },
  {
    path: "/lab/ui/visits/photos-media",
    title: "Visit Photo Media",
    description:
      "Preview canonical visit photo rows and edit drawer content with resolver-backed storage-path media.",
  },
];

function MetaPageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const telemetryPathname = redactSensitiveRoutePathname(location.pathname);
    const eventSourceUrl = `${window.location.origin}${telemetryPathname}${location.search}`;

    // Codex drift guard: keep PageView tracking post-paint/idle like the Pixel
    // bootstrap, but route it through trackMetaEvent so Pixel and CAPI share IDs.
    const trackPageView = () => {
      trackMetaEvent({
        event_name: "PageView",
        event_source_url: eventSourceUrl,
        source_context: "router.page_view",
      }).catch(() => {});
    };

    let idleId = null;
    const timeoutId = window.setTimeout(() => {
      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(trackPageView, {
          timeout: 2500,
        });
        return;
      }

      trackPageView();
    }, 3000);

    return () => {
      window.clearTimeout(timeoutId);
      if (idleId && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [location.pathname, location.search]);

  return null;
}

function ExperimentPreviewGate() {
  const location = useLocation();
  const previewState = getExperimentPreviewState({
    search: location.search,
    pathname: location.pathname,
  });

  if (!previewState.isPreviewConsoleVisible) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <ExperimentPreviewConsole />
    </Suspense>
  );
}

function PreOnboardingRedirect({ variantKey = "" }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search || "");

  params.set("start", "pre-onboarding");
  if (variantKey) {
    params.set(
      "ll_exp",
      `funnel_pre_onboarding_flow_version_v1:${variantKey}`,
    );
  }

  return <Navigate to={`/?${params.toString()}`} replace />;
}

function App() {
  const memberSeoTaskSlugs = getMemberSeoTaskSlugs();
  const memberSeoTaskRootRoutes = getMemberSeoTaskRootRoutes();
  useInstalledPwaPortraitLock();

  useEffect(() => {
    captureAdAttributionFromUrl();

    // Codex drift guard: conversion scripts are intentionally post-paint/idle
    // so they do not compete with public LCP.
    const loadConversionTags = () => {
      configureGoogleAdsTag();
      configureMetaPixel();
    };

    let idleId = null;
    const timeoutId = window.setTimeout(() => {
      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(loadConversionTags, {
          timeout: 2500,
        });
        return;
      }

      loadConversionTags();
    }, 3000);

    return () => {
      window.clearTimeout(timeoutId);
      if (idleId && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, []);

  const withSuspense = (element) => (
    <Suspense fallback={null}>{element}</Suspense>
  );

  return (
    <Router>
      <CrashRouteObserver />
      <BuildRefreshGate />
      <MetaPageViewTracker />
      <ExperimentPreviewGate />
      <ScrollToTop />
      <AppRuntimeProviders>
        <VisitMarginExperimentProvider>
        <BbbBrandingExperimentProvider>
        <StickyBarHost />
        <Routes>
          {/* Public routes */}
          <Route
            path="/"
            element={withSuspense(
              <Landing
                seoPath="/"
                requireRoleSelection={true}
                useWebsiteFooter={true}
              />,
            )}
          />
          {PUBLIC_PUZZLES_ENABLED && <Route path="/puzzles" element={withSuspense(<PuzzlesPage />)} />}
          {PUBLIC_PUZZLES_ENABLED && <Route path="/puzzles/:gameId" element={withSuspense(<PuzzlesPage />)} />}
          <Route path="/home" element={withSuspense(<Home />)} />
          <Route path="/share" element={withSuspense(<SharePage />)} />
          <Route path="/s/:format" element={withSuspense(<ShareQrLanding />)} />
          <Route
            path="/parent-authorization"
            element={withSuspense(<ParentAuthorizationPage />)}
          />
          <Route
            path="/senior-help"
            element={<Navigate to="/senior-household-help" replace />}
          />
          <Route path="/pricing" element={withSuspense(<MembershipPage />)} />
          <Route
            path="/confirmed"
            element={withSuspense(<ConfirmationPage />)}
          />

          <Route
            path="/legal"
            element={
              <Suspense>
                <LegalLayout />
              </Suspense>
            }
          >
            <Route
              index
              element={
                <Suspense>
                  <LegalIndexPage documents={legalDocuments} />
                </Suspense>
              }
            />
            <Route
              path=":slug"
              element={
                <Suspense>
                  <LegalDocumentPage documents={legalDocuments} />
                </Suspense>
              }
            />
          </Route>

          <Route path="/locations" element={withSuspense(<LocationsPage />)} />
          <Route path="/maps" element={withSuspense(<MapsPage />)} />
          <Route path="/404" element={withSuspense(<NotFound />)} />

          <Route
            path="/task-search"
            element={withSuspense(<TaskSearchPage />)}
          />
          <Route path="/services" element={withSuspense(<ServicesPage />)} />
          {memberSeoTaskRootRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={withSuspense(
                <MemberSeoTaskRootPage
                  taskSlug={route.memberSeoTask.taskSlug}
                />,
              )}
            />
          ))}
          {memberSeoTaskRootRoutes
            .filter((route) => route.serviceRoutePath)
            .map((route) => (
              <Route
                key={`${route.serviceRoutePath}-member-seo`}
                path={route.serviceRoutePath}
                element={withSuspense(
                  <MemberSeoTaskRootPage
                    taskSlug={route.memberSeoTask.taskSlug}
                  />,
                )}
              />
            ))}
          {memberSeoTaskSlugs.map((taskSlug) => (
            <Route
              key={taskSlug}
              path={`/${taskSlug}/:stateSlug/:citySlug`}
              element={withSuspense(<MemberSeoCityPage taskSlug={taskSlug} />)}
            />
          ))}
          <Route
            path="/services/:serviceSlug"
            element={withSuspense(<ServiceDetailPage />)}
          />
          <Route path="/about" element={withSuspense(<AboutPage />)} />
          <Route path="/get-started" element={<PreOnboardingRedirect />} />
          <Route path="/get-started-v3" element={<PreOnboardingRedirect variantKey="pre_onboarding_v3" />} />
          <Route
            path="/join-waitlist"
            element={withSuspense(<LoginOrSignUp title="Join waitlist" />)}
          />
          <Route path="/ad-copy" element={withSuspense(<AdCopyPage />)} />
          <Route path="/ads/:helperId" element={withSuspense(<AdsView />)} />
          <Route
            path="/review/:reviewId"
            element={withSuspense(<ReviewView />)}
          />

          <Route path="/signup" element={<PreOnboardingRedirect />} />
          <Route path="/login" element={withSuspense(<GeneralLogin />)} />
          {process.env.NODE_ENV === "development" && (
            <Route
              path="/agent/booking"
              element={withSuspense(<SeniorAgentBookingPage />)}
            />
          )}
          <Route path="/admin/agent-connect" element={withSuspense(<AgentConnectPage />)} />
          {/* <Route
          path="/spin-to-win"
          element={
            <>
              <GuestHeader />
              <WheelPopup fullScreen={true} />
              <WebFooter />
            </>
          }
        /> */}

          <Route path="/env" element={<EnvironmentInfo />} />
          <Route
            path="/lab/ingenuity-admins"
            element={withSuspense(<IngenuityAdminsPage />)}
          />
          <Route
            path="/lab/ui"
            element={withSuspense(<LocalUiLabIndexPage labs={localUiLabs} />)}
          />
          {process.env.NODE_ENV === "development" && (
            <Route
              path="/lab/ui/dashboard-sign-in-loading"
              element={withSuspense(<LoadingUserLabPage />)}
            />
          )}
          <Route
            path="/lab/chat"
            element={withSuspense(<PublicChatComposerLabPage />)}
          />
          <Route
            path="/lab/chat/:version"
            element={withSuspense(<PublicChatComposerLabPage />)}
          />
          <Route
            path="/lab/ui/helper-search-grid"
            element={withSuspense(<HelperSearchGridLabPage />)}
          />
          <Route
            path="/lab/ui/platform/google-places-autocomplete"
            element={withSuspense(<GooglePlacesAutocompleteLabPage />)}
          />
          <Route
            path="/lab/ui/helper-founder-welcome"
            element={withSuspense(<HelperFounderWelcomeLabPage />)}
          />
          <Route
            path="/lab/ui/helper-discounts"
            element={withSuspense(<HelperDiscountsLabPage />)}
          />
          <Route
            path="/lab/ui/helpers/rich-profile"
            element={withSuspense(<HelperRichProfileLabPage />)}
          />
          <Route
            path="/lab/ui/helpers/in-app-orientation"
            element={withSuspense(<InAppOrientationPlaygroundPage />)}
          />
          {process.env.NODE_ENV === "development" && (
            <Route
              path="/lab/ui/helpers/hiring-decline-review"
              element={withSuspense(<HiringDeclineLabPage />)}
            />
          )}
          <Route
            path="/lab/ui/helpers/optional-merch-final-media"
            element={withSuspense(<HelperMerchOptionalPlaygroundPage />)}
          />
          <Route
            path="/lab/ui/helpers/parent-authorization"
            element={withSuspense(<ParentAuthorizationLabPage />)}
          />
          <Route
            path="/lab/ui/legal/acceptance-changes"
            element={withSuspense(<LegalUiChangesLabPage />)}
          />
          {process.env.NODE_ENV === "development" && (
            <Route path="/lab/ui/member/stamp-card" element={withSuspense(<MemberStampCardLabPage />)} />
          )}
          <Route
            path="/lab/ui/member/founder-welcome-card"
            element={withSuspense(<MemberFounderWelcomeCardLabPage />)}
          />
          {process.env.NODE_ENV === "development" && (
            <Route
              path="/lab/ui/member/home-all-cards"
              element={withSuspense(<MemberHomeAllCardsLabPage />)}
            />
          )}
          <Route
            path="/lab/ui/member/onboarding-flow"
            element={withSuspense(<MemberOnboardingFlowLabPage />)}
          />
          <Route
            path="/lab/ui/member/address-flow"
            element={withSuspense(<MemberAddressFlowLabPage />)}
          />
          <Route
            path="/lab/ui/member/core-info-repair"
            element={withSuspense(<MemberCoreInfoRepairLabPage />)}
          />
          <Route
            path="/lab/ui/member/dashboard-v2"
            element={withSuspense(<MemberDashboardV2LabPage />)}
          />
          <Route
            path="/lab/ui/member/share-campaign"
            element={withSuspense(<ShareCampaignLabPage />)}
          />
          <Route
            path="/lab/ui/member/billed-visits"
            element={withSuspense(<MemberBillingVisitsLabPage />)}
          />
          <Route
            path="/lab/ui/member/experience-telemetry"
            element={withSuspense(<MemberExperienceTelemetryLabPage />)}
          />
          <Route
            path="/lab/ui/member/schedule-visit-flow"
            element={withSuspense(<ScheduleVisitFlowLabPage />)}
          />
          <Route
            path="/lab/ui/member/checkout-variations"
            element={withSuspense(<CheckoutVariationsLabPage />)}
          />
          <Route
            path="/lab/ui/member/first-booking-experiments"
            element={withSuspense(<MemberFirstBookingExperimentsLabPage />)}
          />
          <Route
            path="/lab/ui/billing/membership-checkout-summary"
            element={withSuspense(<MembershipCheckoutSummaryLabPage />)}
          />
          <Route
            path="/lab/ui/member/pay-what-you-can-card"
            element={withSuspense(<MemberPWYCCardLabPage />)}
          />
          <Route
            path="/lab/ui/member/annual-membership-contribution"
            element={withSuspense(<AnnualMembershipContributionLabPage />)}
          />
          <Route
            path="/lab/ui/public/landing-section-order"
            element={withSuspense(<LandingSectionOrderLabPage />)}
          />
          <Route
            path="/lab/ui/public/landing-pwyc-pricing"
            element={withSuspense(<LandingPwycPricingLabPage />)}
          />
          <Route
            path="/lab/ui/pre-onboarding/choose-rate"
            element={withSuspense(<PreOnboardingChooseRateLabPage />)}
          />
          <Route
            path="/lab/ui/pre-onboarding/v2-membership"
            element={withSuspense(<PreOnboardingV2MembershipLabPage />)}
          />
          <Route
            path="/lab/ui/pre-onboarding/how-it-works"
            element={withSuspense(<PreOnboardingHowItWorksLabPage />)}
          />
          <Route
            path="/lab/ui/helpers/rates"
            element={withSuspense(<HelperRateCardLabPage />)}
          />
          <Route
            path="/lab/ui/helpers/rate-settings"
            element={withSuspense(<HelperRateSettingsLabPage />)}
          />
          <Route
            path="/lab/ui/public/local-pricing-distribution"
            element={withSuspense(<LocalPricingDistributionLabPage />)}
          />
          <Route
            path="/lab/ui/helpers/visit-pay-rates"
            element={withSuspense(<VisitHelperRateLabPage />)}
          />
          <Route
            path="/lab/ui/helpers/area-status"
            element={withSuspense(<HelperAreaStatusLabPage />)}
          />
          <Route
            path="/lab/ui/helpers/rate-rewards"
            element={withSuspense(<HelperRateRewardsLabPage />)}
          />
          <Route
            path="/lab/ui/member/membership-retention-card"
            element={withSuspense(<MemberMembershipRetentionLabPage />)}
          />
          <Route
            path="/lab/ui/pre-onboarding/v2-booking-states"
            element={withSuspense(<PreOnboardingV2StatesLabPage />)}
          />
          <Route
            path="/lab/ui/pre-onboarding/v3"
            element={withSuspense(<PreOnboardingV3LabPage />)}
          />
          <Route
            path="/lab/ui/member/billing-rate"
            element={withSuspense(<MemberBillingRateLabPage />)}
          />
          <Route
            path="/lab/ui/pre-onboarding/account-contact-card"
            element={withSuspense(<PreOnboardingAccountContactCardLabPage />)}
          />
          <Route
            path="/lab/ui/pre-onboarding/waitlist-flow"
            element={withSuspense(<PreOnboardingWaitlistFlowLabPage />)}
          />
          <Route
            path="/lab/ui/pre-onboarding/location-availability"
            element={withSuspense(<PreOnboardingLocationAvailabilityLabPage />)}
          />
          <Route
            path="/lab/ui/login-recovery"
            element={withSuspense(<LoginRecoveryLabPage />)}
          />
          <Route
            path="/lab/ui/helpers/member-card-actions"
            element={withSuspense(<HelperMemberCardActionsLabPage />)}
          />
          <Route
            path="/lab/ui/helpers/manual-create-visit"
            element={withSuspense(<ManualCreateVisitLabPage />)}
          />
          <Route
            path="/lab/ui/helpers/local-momentum"
            element={withSuspense(<LocalHelperMomentumLabPage />)}
          />
          <Route
            path="/lab/ui/helpers/referrals-tab"
            element={withSuspense(<HelperReferralsTabLabPage />)}
          />
          <Route
            path="/lab/ui/helpers/flyer-challenge"
            element={withSuspense(<FlyerChallengeLabPage />)}
          />
          <Route
            path="/lab/ui/public/hero-photo-options"
            element={withSuspense(<PublicHeroPhotoOptionsLabPage />)}
          />
          <Route
            path="/lab/ui/public/demand-map"
            element={withSuspense(<PublicDemandMapLabPage />)}
          />
          {process.env.NODE_ENV === "development" && (
            <Route path="/lab/ui/public/credibility-counters" element={withSuspense(<CredibilityCountersLabPage />)} />
          )}
          <Route
            path="/lab/ui/public/helper-selectivity"
            element={withSuspense(<HelperSelectivityConceptsLabPage />)}
          />
          <Route
            path="/lab/ui/public/helper-discount-sticky-bar"
            element={withSuspense(<LandingHelperDiscountStickyBarLabPage />)}
          />
          <Route
            path="/lab/ui/public/credibility-sticky-bar"
            element={withSuspense(<LandingCredibilityStickyBarLabPage />)}
          />
          <Route
            path="/lab/ui/sticky-bar-system"
            element={withSuspense(<StickyBarSystemLabPage />)}
          />
          <Route
            path="/lab/ui/public/review-detail-lines"
            element={withSuspense(<PublicReviewDetailsLabPage />)}
          />
          <Route
            path="/lab/ui/public/facebook-review-image"
            element={withSuspense(<FacebookReviewImageLabPage />)}
          />
          <Route
            path="/lab/ui/messages/support"
            element={withSuspense(<SupportMessagePreviewPage />)}
          />
          <Route
            path="/lab/ui/messages/member-surfaces"
            element={withSuspense(<MemberMessageSurfacesLabPage />)}
          />
          <Route
            path="/lab/ui/dashboard-feedback"
            element={withSuspense(<DashboardFeedbackLabPage />)}
          />
          <Route
            path="/lab/ui/member/schedule-visit-exit-feedback"
            element={withSuspense(<ScheduleVisitExitFeedbackLabPage />)}
          />
          <Route
            path="/lab/ui/visits/photos-media"
            element={withSuspense(<VisitPhotoMediaLabPage />)}
          />
          <Route
            path="lab/circles"
            element={<Navigate to="/lab/retargetingGeo/tiers" replace />}
          />
          <Route
            path="lab/retargetingGeo"
            element={<Navigate to="/lab/retargetingGeo/tiers" replace />}
          />
          <Route
            path="lab/retargetingGeo/tiers"
            element={withSuspense(<TieredGeoRetargetingLabPage />)}
          />

          <Route
            path="/member/signup"
            element={withSuspense(<MemberSignUpPage />)}
          />
          <Route
            path="/member/login"
            element={withSuspense(<GeneralLogin />)}
          />
          <Route path="/contact" element={withSuspense(<ContactPage />)} />
          <Route path="/inquiry" element={<Navigate to="/contact" replace />} />
          <Route path="/faq" element={withSuspense(<FaqPage />)} />
          <Route path="/hiring" element={withSuspense(<HiringPage />)} />
          <Route
            path="/hiring/locations"
            element={withSuspense(<HiringLocationsHubPage />)}
          />
          <Route
            path="/hiring/:stateSlug"
            element={withSuspense(<HiringStatePage />)}
          />
          <Route
            path="/hiring/:stateSlug/:citySlug"
            element={withSuspense(<HiringCityPage />)}
          />
          <Route
            path="/flyers/hiring"
            element={withSuspense(<HiringFlyer />)}
          />
          <Route
            path="/flyers/seniors"
            element={withSuspense(<SeniorFlyer />)}
          />

          <Route path="/invites" element={withSuspense(<InvitesPage />)} />
          <Route path="/auth" element={withSuspense(<AuthPage />)} />
          <Route path="/review" element={withSuspense(<ReviewPage />)} />
          <Route path="/reviews" element={withSuspense(<ReviewsPage />)} />
          <Route path="/helpers" element={withSuspense(<HelpersPage />)} />

          {/* <Route path="/register" element={<RegisterPage />} /> */}

          {/* Test routes */}
          <Route
            path="/helper/signup"
            element={withSuspense(<HelperSignupView standalone={true} />)}
          />
          <Route
            path="/helper/login"
            element={withSuspense(<GeneralLogin />)}
          />
          <Route
            path="/join"
            element={withSuspense(
              <ShareJoinAttribution><Landing seoPath="/join" requireRoleSelection={true} /></ShareJoinAttribution>,
            )}
          />
          <Route
            path="/dashboard"
            element={withSuspense(
              <SnackbarProvider>
                <DashboardPage />
              </SnackbarProvider>,
            )}
          />

          {/* Redirects */}
          <Route path="/about-us" element={<Navigate to="/about" replace />} />
          <Route
            path="/membership"
            element={<Navigate to="/pricing" replace />}
          />
          <Route
            path="/household-help"
            element={<Navigate to="/senior-household-help" replace />}
          />
          <Route
            path="/job-openings/link-helper"
            element={<Navigate to="/hiring" replace />}
          />

          {/* Redirect if page not found */}
          <Route path="*" element={withSuspense(<NotFound />)} />
        </Routes>
          <Suspense fallback={null}>
            <DrawerContainer />
          </Suspense>
        </BbbBrandingExperimentProvider>
        </VisitMarginExperimentProvider>
      </AppRuntimeProviders>
    </Router>
  );
}

export default App;
